import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { OrderService } from '../../services/order.service';

export interface Promo {
  id: number;
  name: string;
  code: string;
  description: string;
  discount_type_id: number; // 1=flat, 2=percentage
  discount_amount: number;
  min_cart_value: number;
  product_type_ids: number[];
  start_at: string;
  expires_at: string;
  status: boolean;
}

@Component({
  selector: 'app-promo-dialog',
  templateUrl: './promo-dialog.component.html'
})
export class PromoDialogComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() cartValue = 0;
  @Output() applied = new EventEmitter<Promo>();
  @Output() closed = new EventEmitter<void>();

  promos: Promo[] = [];
  loading = false;
  manualCode = '';
  error = '';

  constructor(private orderService: OrderService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && this.isOpen) {
      document.body.style.overflow = 'hidden';
      this.loadPromos();
    }
    if (changes['isOpen'] && !this.isOpen) {
      document.body.style.overflow = '';
    }
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
  }

  loadPromos() {
    this.loading = true;
    this.error = '';
    this.orderService.getPromos().subscribe({
      next: (res: any) => {
        let data: any = res?.data ?? res ?? [];
        if (data && !Array.isArray(data)) data = data.data || data.results || [];
        this.promos = Array.isArray(data) ? data : [];
        this.loading = false;
      },
      error: () => {
        this.promos = [];
        this.loading = false;
      }
    });
  }

  isPromoDisabled(promo: Promo): boolean {
    if (this.cartValue < promo.min_cart_value) return true;
    return false;
  }

  getMinCartMessage(promo: Promo): string {
    if (this.cartValue < promo.min_cart_value) {
      return `Min cart value required: ₹${promo.min_cart_value}`;
    }
    return '';
  }

  applyPromo(promo: Promo) {
    if (this.isPromoDisabled(promo)) return;
    this.applied.emit(promo);
    this.close();
  }

  applyManualCode() {
    if (!this.manualCode.trim()) {
      this.error = 'Please enter a promo code';
      return;
    }
    this.error = '';

    // Check if code matches a loaded promo
    const match = this.promos.find(p => p.code.toLowerCase() === this.manualCode.trim().toLowerCase());
    if (match) {
      if (this.isPromoDisabled(match)) {
        this.error = this.getMinCartMessage(match) || 'This coupon is not applicable';
        return;
      }
      this.applied.emit(match);
      this.close();
      return;
    }

    // If not in list, try API lookup
    this.loading = true;
    this.orderService.getPromos().subscribe({
      next: (res: any) => {
        const data = res?.data || res || [];
        const found = (Array.isArray(data) ? data : []).find(
          (p: Promo) => p.code.toLowerCase() === this.manualCode.trim().toLowerCase() && p.status && p.discount_amount > 0
        );
        this.loading = false;
        if (!found) {
          this.error = 'No promo code available with this code';
          return;
        }
        if (this.cartValue < found.min_cart_value) {
          this.error = `Min cart value required: ₹${found.min_cart_value}`;
          return;
        }
        this.applied.emit(found);
        this.close();
      },
      error: () => {
        this.error = 'Failed to verify promo code. Please try again.';
        this.loading = false;
      }
    });
  }

  close() {
    document.body.style.overflow = '';
    this.manualCode = '';
    this.error = '';
    this.closed.emit();
  }
}
